import React, { useState } from 'react'
import { Card, Form, Switch, Select, Button, Typography, Space, message, Divider, InputNumber, Tabs } from 'antd'
import { SettingOutlined, SaveOutlined, DownloadOutlined, ApiOutlined } from '@ant-design/icons'
import ProxySettings from './ProxySettings'

const { Title, Text } = Typography
const { Option } = Select

interface SettingsForm {
  theme: 'light' | 'dark' | 'auto'
  language: 'zh-CN' | 'en-US'
  pageSize: number
  autoRefresh: boolean
  refreshInterval: number
  notifications: boolean
  emailNotifications: boolean
  exportFormat: 'csv' | 'json' | 'excel'
}

const SettingsPage: React.FC = () => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  // 默认设置
  const defaultSettings: SettingsForm = {
    theme: 'light',
    language: 'zh-CN',
    pageSize: 20,
    autoRefresh: false,
    refreshInterval: 30,
    notifications: true,
    emailNotifications: false,
    exportFormat: 'csv'
  }

  const onFinish = async (values: SettingsForm) => {
    try {
      setLoading(true)
      window.logger?.info('Saving settings', values)

      // 保存到localStorage
      localStorage.setItem('admin-settings', JSON.stringify(values))
      
      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      message.success('设置保存成功')
      window.logger?.info('Settings saved successfully')
    } catch (error: any) {
      window.logger?.error('Settings save failed', error)
      message.error('保存失败: ' + (error.message || '未知错误'))
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    form.setFieldsValue(defaultSettings)
    message.info('已重置为默认设置')
  }

  const handleExportSettings = () => {
    try {
      const settings = form.getFieldsValue()
      const exportData = {
        exportTime: new Date().toISOString(),
        settings
      }
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `admin-settings-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      
      message.success('设置导出成功')
      window.logger?.info('Settings exported successfully')
    } catch (error: any) {
      window.logger?.error('Settings export failed', error)
      message.error('导出失败')
    }
  }

  // 初始化表单值
  React.useEffect(() => {
    try {
      const savedSettings = localStorage.getItem('admin-settings')
      if (savedSettings) {
        const settings = JSON.parse(savedSettings)
        form.setFieldsValue({ ...defaultSettings, ...settings })
      } else {
        form.setFieldsValue(defaultSettings)
      }
    } catch (error) {
      form.setFieldsValue(defaultSettings)
    }
  }, [form])

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <SettingOutlined style={{ marginRight: '8px' }} />
          系统设置
        </Title>
        <Text type="secondary">配置管理后台的显示和行为设置</Text>
      </div>

      <Tabs
        defaultActiveKey="general"
        items={[
          {
            key: 'general',
            label: (
              <Space>
                <SettingOutlined />
                常规设置
              </Space>
            ),
            children: (
              <Card>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          <Title level={4}>界面设置</Title>
          
          <Form.Item
            label="主题模式"
            name="theme"
          >
            <Select>
              <Option value="light">浅色主题</Option>
              <Option value="dark">深色主题</Option>
              <Option value="auto">跟随系统</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="语言"
            name="language"
          >
            <Select>
              <Option value="zh-CN">简体中文</Option>
              <Option value="en-US">English</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="每页显示条数"
            name="pageSize"
          >
            <InputNumber min={10} max={100} step={10} style={{ width: '100%' }} />
          </Form.Item>

          <Divider />

          <Title level={4}>数据刷新</Title>

          <Form.Item
            label="自动刷新"
            name="autoRefresh"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label="刷新间隔（秒）"
            name="refreshInterval"
          >
            <InputNumber min={10} max={300} step={10} style={{ width: '100%' }} />
          </Form.Item>

          <Divider />

          <Title level={4}>通知设置</Title>

          <Form.Item
            label="浏览器通知"
            name="notifications"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label="邮件通知"
            name="emailNotifications"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Divider />

          <Title level={4}>导出设置</Title>

          <Form.Item
            label="默认导出格式"
            name="exportFormat"
          >
            <Select>
              <Option value="csv">CSV</Option>
              <Option value="json">JSON</Option>
              <Option value="excel">Excel</Option>
            </Select>
          </Form.Item>

                <div style={{ textAlign: 'right', marginTop: '32px' }}>
                  <Space>
                    <Button onClick={handleReset}>
                      重置默认
                    </Button>
                    <Button
                      onClick={handleExportSettings}
                      icon={<DownloadOutlined />}
                    >
                      导出设置
                    </Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      icon={<SaveOutlined />}
                    >
                      保存设置
                    </Button>
                  </Space>
                </div>
              </Form>
              </Card>
            ),
          },
          {
            key: 'proxy',
            label: (
              <Space>
                <ApiOutlined />
                代理设置
              </Space>
            ),
            children: <ProxySettings />,
          },
        ]}
      />
    </div>
  )
}

export default SettingsPage
